import Alpine from "alpinejs";

Alpine.data("ordersGrid", () => ({
  ready: false,
  sectionId: null,
  _orders: [],
  get orders() {
    const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
    const end = start + this.pagination.perPage;

    return (
      this._orders
        // Filter orders based on the selected status (pending or complete)
        .filter((order) => {
          if (this.status === "pending") {
            return order.fulfillment_status !== "complete";
          }

          return order.fulfillment_status === "complete";
        })
        // Sort the filtered orders based on the selected sorting field and direction
        .sort((a, b) => {
          const field = this.sorting.field;
          const direction = this.sorting.direction === "asc" ? 1 : -1;

          const getValue = (obj, path) => {
            return path.split(".").reduce((acc, part) => acc && acc[part], obj);
          };

          const aValue = getValue(a, field);
          const bValue = getValue(b, field);

          if (aValue < bValue) return -1 * direction;
          if (aValue > bValue) return 1 * direction;
          return 0;
        })
        // Return only the orders for the current page based on pagination settings
        .slice(start, end)
    );
  },
  sorting: {
    field: "created_at.raw",
    direction: "desc",
  },
  pagination: {
    perPage: 20,
    currentPage: 1,
    totalPages: 1,
  },
  status: "pending",
  init() {
    this.getOrders();

    // Keep pagination state valid when filters and sorting change.
    this.$watch("status", () => {
      this.pagination.currentPage = 1;
      this.recalculatePagination();
    });

    this.$watch("sorting.field", () => {
      this.pagination.currentPage = 1;
      this.recalculatePagination();
    });

    this.$watch("sorting.direction", () => {
      this.pagination.currentPage = 1;
      this.recalculatePagination();
    });
  },

  recalculatePagination() {
    this.pagination.totalPages = Math.max(
      1,
      Math.ceil(
        this._orders.filter((order) => {
          if (this.status === "pending") {
            return order.fulfillment_status !== "complete";
          }

          return order.fulfillment_status === "complete";
        }).length / this.pagination.perPage,
      ),
    );

    this.pagination.currentPage = Math.min(
      this.pagination.currentPage,
      this.pagination.totalPages,
    );
  },

  setSorting(field, direction) {
    this.sorting.field = field;
    this.sorting.direction = direction;
  },

  isSortedBy(field, direction) {
    return this.sorting.field === field && this.sorting.direction === direction;
  },

  getOrders() {
    this.ready = false;
    this._orders = [];
    this.pagination.currentPage = 1;
    this.pagination.totalPages = 1;

    const getPage = (pageNumber = 1, pageParam = "page") => {
      const url = new URL(window.location.origin);

      if (!this.sectionId) {
        this.sectionId = document
          .querySelector(".shopify-section[id*='orders-js']")
          .id.replace("shopify-section-", "");
      }

      url.searchParams.set("sections", this.sectionId);
      url.searchParams.set(pageParam, pageNumber);

      return fetch(url.toString())
        .then((response) => response.json())
        .then((sections) => {
          const html = sections[this.sectionId];
          if (!html) {
            return;
          }

          const parser = new DOMParser();
          const dataScript = parser
            .parseFromString(html, "text/html")
            .querySelector('script[type="application/json"]');

          if (!dataScript) {
            return;
          }

          const payload = JSON.parse(dataScript.textContent || "{}");
          const pageOrders = Array.isArray(payload.data) ? payload.data : [];
          this._orders = [...this._orders, ...pageOrders];

          const pagination = payload.pagination;
          if (!pagination) {
            return;
          }

          const currentPage = Number(pagination.current_page) || pageNumber;
          const totalPages = Number(pagination.total_pages) || currentPage;
          const nextPageParam = pagination.page_param || pageParam;

          if (currentPage < totalPages) {
            return getPage(currentPage + 1, nextPageParam);
          }
        });
    };

    getPage()
      .then(() => {
        this.recalculatePagination();

        if (this._orders.length === 0) {
          console.log("No orders found.");
        }
      })
      .catch((error) => {
        console.error("Failed to fetch orders.", error);
      })
      .finally(() => {
        this.ready = true;
      });
  },

  productAvailabilityMap: [],
  productAvailabilityRequests: [],

  getReorderLink(order) {
    const url = new URL(window.location.origin);

    const availableItems = order.line_items.filter((item) => {
      // do a quick HEAD check for a 404
      const availability = this.productAvailabilityMap.find(
        (product) => product.id === item.variant.id,
      );

      if (!availability) {
        const requestInFlight = this.productAvailabilityRequests.includes(
          item.variant.id,
        );

        if (!requestInFlight) {
          this.productAvailabilityRequests.push(item.variant.id);

          fetch(item.variant.url, { method: "HEAD" })
            .then((response) => {
              this.productAvailabilityMap.push({
                id: item.variant.id,
                available: response.ok,
              });
            })
            .catch((error) => {
              console.error(
                `Failed to check availability for product ${item.variant.id}.`,
                error,
              );
            })
            .finally(() => {
              this.productAvailabilityRequests =
                this.productAvailabilityRequests.filter(
                  (id) => id !== item.variant.id,
                );
            });
        }

        return true;
      }

      return availability.available;
    });

    if (availableItems.length === 0) {
      return null;
    }

    url.pathname =
      "/cart/" +
      availableItems
        .map((item) => `${item.variant.id}:${item.quantity}`)
        .join(",");

    return url.toString();
  },
}));
